/**
 * Organization section: Company Name + Brand (white-label) UI.
 * Relocated from SettingsPage → Brand tab. Reads/writes organizations.name and organizations.brand_profile.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Loader2, Save } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { useBrand } from '@/hooks/useBrand';
import { BrandedLogo } from '@/components/brand/BrandedLogo';
import { supabase } from '@/integrations/supabase/client';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import { toast } from 'sonner';
import type { BrandProfile } from '@/lib/brand-types';

/** Default brand accent hex — must match --brand-accent in index.css */
const BRAND_ACCENT_HEX = '#14b8a6';

export function OrganizationBrandSection() {
  const { organization, refreshData } = useApp();
  const { brandProfile, companyName, logoUrl, isBranded } = useBrand();

  const [websiteUrl, setWebsiteUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState<{ confidence?: string; error?: string } | null>(null);

  const [editOrgName, setEditOrgName] = useState('');
  const [editAccent, setEditAccent] = useState('');
  const [editPoweredBy, setEditPoweredBy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editLogoUrl, setEditLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    setEditOrgName(organization?.name ?? '');
    setEditAccent(brandProfile?.accent_color ?? brandProfile?.primary_color ?? '');
    setEditPoweredBy(brandProfile?.powered_by_wt ?? true);
    setEditLogoUrl(brandProfile?.logo_url ?? null);
  }, [organization?.name, brandProfile]);

  const handleExtract = async () => {
    if (!websiteUrl.trim() || !organization) return;
    setExtracting(true);
    setExtractResult(null);
    try {
      const result = await invokeAuthedFunction<{ brand_profile?: BrandProfile; confidence?: string; error?: string }>(
        supabase,
        'extract-brand',
        { website_url: websiteUrl.trim(), org_id: organization.id },
      );
      if (result?.error) {
        setExtractResult({ error: result.error });
      } else {
        setExtractResult({ confidence: result?.confidence ?? 'low' });
        await refreshData();
      }
    } catch (err) {
      console.error('Brand extraction failed:', err);
      const message =
        err instanceof Error ? err.message : typeof err === 'string' ? err : 'Extraction failed. You can configure brand settings manually below.';
      setExtractResult({ error: message });
    } finally {
      setExtracting(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !organization) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2 MB');
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() ?? 'png';
      const path = `${organization.id}/logo.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('brand-assets')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('brand-assets').getPublicUrl(path);
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      setEditLogoUrl(publicUrl);
      toast.success('Logo uploaded');
    } catch (err) {
      console.error('Logo upload failed:', err);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!organization) return;
    setSaving(true);
    try {
      const profile: BrandProfile = {
        company_name: editOrgName.trim() || organization.name,
        logo_url: editLogoUrl,
        primary_color: editAccent || null,
        accent_color: editAccent || null,
        powered_by_wt: editPoweredBy,
        website: brandProfile?.website ?? (websiteUrl.trim() || null),
        extracted_at: brandProfile?.extracted_at ?? null,
      };
      const { error } = await supabase
        .from('organizations')
        .update({
          name: editOrgName.trim() || organization.name,
          brand_profile: profile as unknown as Record<string, unknown>,
        })
        .eq('id', organization.id);
      if (error) throw error;
      await refreshData();
      toast.success('Organization and brand settings saved');
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!organization) return;
    setSaving(true);
    try {
      await supabase
        .from('organizations')
        .update({ brand_profile: null })
        .eq('id', organization.id);
      await refreshData();
      toast.success('Brand reset to Watt Tower defaults');
    } catch (err) {
      console.error('Reset failed:', err);
      toast.error('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  if (!organization) return null;

  return (
    <div className="space-y-6">
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader>
          <CardTitle className="text-lg">Company Name</CardTitle>
          <CardDescription>Display name for your organization.</CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            value={editOrgName}
            onChange={(e) => setEditOrgName(e.target.value)}
            placeholder="Your company"
            className="bg-wt-bg-tertiary border-border max-w-md"
          />
        </CardContent>
      </Card>

      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader>
          <CardTitle className="text-lg">Extract Brand from Website</CardTitle>
          <CardDescription>Paste your company website and we&apos;ll extract logo, colors, and name.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Input
              placeholder="https://yourcompany.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              className="bg-wt-bg-tertiary border-border"
            />
            <Button onClick={handleExtract} disabled={extracting || !websiteUrl.trim()} className="shrink-0">
              {extracting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Extract Brand
            </Button>
          </div>
          {extractResult?.error && <p className="text-sm text-wt-red-primary">{extractResult.error}</p>}
          {extractResult?.confidence && (
            <p className="text-sm text-wt-green-primary">Brand extracted (confidence: {extractResult.confidence}). Review below.</p>
          )}
        </CardContent>
      </Card>

      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader>
          <CardTitle className="text-lg">Brand Configuration</CardTitle>
          <CardDescription>Customize how your organization appears to homeowners.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-center gap-4">
              {editLogoUrl ? (
                <img src={editLogoUrl} alt="Brand logo" className="h-12 w-auto object-contain rounded bg-wt-bg-tertiary p-1" />
              ) : (
                <div className="h-12 w-12 rounded bg-wt-bg-tertiary flex items-center justify-center text-muted-foreground text-xs">No logo</div>
              )}
              <div>
                <label className="cursor-pointer">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border border-border bg-wt-bg-tertiary hover:bg-wt-bg-secondary transition-colors">
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Upload Logo
                  </span>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
                </label>
                <p className="text-xs text-muted-foreground mt-1">PNG, JPG, or SVG. Max 2 MB.</p>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="brand-accent">Accent Color</Label>
            <div className="flex items-center gap-3 max-w-md">
              <input
                type="color"
                value={editAccent || BRAND_ACCENT_HEX}
                onChange={(e) => setEditAccent(e.target.value)}
                className="w-10 h-10 rounded border border-border cursor-pointer bg-transparent"
              />
              <Input
                id="brand-accent"
                value={editAccent}
                onChange={(e) => setEditAccent(e.target.value)}
                placeholder="#1E40AF"
                className="bg-wt-bg-tertiary border-border font-mono"
              />
            </div>
          </div>
          <div className="flex items-center justify-between max-w-md">
            <div>
              <Label htmlFor="brand-powered-by">Show &quot;Powered by Watt Tower&quot;</Label>
              <p className="text-xs text-muted-foreground">Visible in sidebar, invite page, and email footer.</p>
            </div>
            <Switch id="brand-powered-by" checked={editPoweredBy} onCheckedChange={setEditPoweredBy} />
          </div>
          <Separator />
          <div>
            <Label className="mb-2 block">Preview</Label>
            <div className="rounded-lg border border-border bg-wt-bg-primary p-4 max-w-xs">
              <div className="flex items-center gap-3">
                {editLogoUrl ? (
                  <img src={editLogoUrl} alt="Preview" className="h-9 w-auto object-contain" />
                ) : (
                  <BrandedLogo size="h-9" />
                )}
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-sidebar-foreground">{editOrgName || companyName || 'Your Company'}</span>
                  {editPoweredBy ? (
                    <span className="text-xs text-muted-foreground">Powered by Watt Tower</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Energy Intelligence</span>
                  )}
                </div>
              </div>
              {editAccent && (
                <div className="mt-3 flex gap-2">
                  <div
                    className="h-8 px-4 rounded text-sm font-medium flex items-center justify-center"
                    style={{
                      backgroundColor: editAccent,
                      color: editAccent ? (parseInt(editAccent.replace('#', ''), 16) > 0x7fffff ? '#0F1115' : '#F8FAFC') : undefined,
                    }}
                  >
                    Button
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
        {isBranded && (
          <Button variant="outline" onClick={handleReset} disabled={saving}>
            Reset to Defaults
          </Button>
        )}
      </div>
    </div>
  );
}
